import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import BackButton from "~/screens/Auth/components/BackButton";
import { AuthLayout } from "~/screens/Auth/Layout/AuthLayout";
import { Step } from "~/screens/get-started/qualifier-questions/-components/step";
import { patientScreeningSchema } from "~/services";
import { usePageTitle } from "~/shared";
import { Input } from "~/ui";

const emailSchema = patientScreeningSchema.pick({ email: true });
type EmailFormSchema = z.infer<typeof emailSchema>;

interface EmailInputProps {
  backRoute: string;
  title: string;
  pageTitle?: string;
  onSubmit: (email: string) => void;
  onBlur?: (email: string) => void;
  externalError?: string;
  isLoading?: boolean;
  buttonText?: string;
}

export const EmailInput = ({
  backRoute,
  title,
  pageTitle,
  onSubmit,
  onBlur,
  externalError,
  isLoading = false,
  buttonText = "Continue",
}: EmailInputProps) => {
  usePageTitle(pageTitle || title);

  const emailForm = useForm<EmailFormSchema>({
    resolver: zodResolver(emailSchema),
  });

  const {
    watch,
    getValues,
    handleSubmit,
    setError,
    register,
    clearErrors,
    formState: { errors: emailErrors },
  } = emailForm;

  const email = watch("email");

  useEffect(() => {
    if (externalError) {
      setError("email", {
        type: "manual",
        message: externalError,
      });
    } else {
      clearErrors("email");
    }
  }, [externalError, setError, clearErrors]);

  const onSubmitForm = (data: EmailFormSchema) => {
    onSubmit(data.email);
  };

  const handleEmailBlur = () => {
    const emailValue = getValues("email");
    if (emailValue && onBlur) {
      onBlur(emailValue);
    }
  };

  return (
    <AuthLayout>
      <section className="md:flex md:w-full md:justify-center">
        <div className="md:w-full md:max-w-md">
          <BackButton variant="violet" to={backRoute} />
          <Step.Root title={title}>
            <div className="flex flex-col gap-3">
              <Input
                id="email"
                required
                label="Email"
                {...register("email")}
                placeholder="example@email.com"
                autoCorrect="off"
                autoComplete="off"
                spellCheck="false"
                error={emailErrors.email?.message}
                onBlur={handleEmailBlur}
              />

              <Step.AgreeToMarketing />
              <Step.Button
                onClick={handleSubmit(onSubmitForm)}
                disabled={!email || isLoading}
              >
                {buttonText}
              </Step.Button>
            </div>
          </Step.Root>
        </div>
      </section>
    </AuthLayout>
  );
};
